import {paperBackgroundColor} from "@buildwithflux/mui-theme";
import React, {MouseEventHandler, ReactNode} from "react";

type Options = {
    backgroundColor: string;
    className: string;
    expanded: boolean;
    expandText: string;
    collapseText: string;
    collapse: boolean;
    ellipsis: boolean;
    ellipsisText: string;
    onExpandClick: MouseEventHandler<HTMLSpanElement>;
    onCollapseClick: MouseEventHandler<HTMLSpanElement>;
    onClick: MouseEventHandler<HTMLSpanElement>;
};

type RequiredProps = {
    previewHeight: string;
    children: ReactNode;
};
type Props = RequiredProps & Partial<Options>;
type ResolvedProps = RequiredProps & Options;

const defaultOptions: Options = {
    backgroundColor: paperBackgroundColor,
    className: "",
    expanded: false,
    expandText: "Expand",
    collapseText: "Collapse",
    collapse: true,
    ellipsis: true,
    ellipsisText: "...",
    onExpandClick: () => {},
    onCollapseClick: () => {},
    onClick: () => {},
};

const CONTENT_HEIGHT_TOLERANCE_PX = 1;

type State = {expanded: boolean; shouldExpand: boolean};

/**
 * Port of the main app's ExpandCollapse (itself adapted from
 * https://github.com/dutiyesh/react-expand-collapse) so search/sitemap cards
 * clamp descriptions identically to production.
 */
class ExpandCollapse extends React.Component<Props, State> {
    toggleContent: HTMLDivElement | null = null;
    resizeObserver: ResizeObserver | null = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            expanded: !!this.props.expanded,
            shouldExpand: true,
        };

        this.handleClick = this.handleClick.bind(this);
        this.shouldDataExpand = this.shouldDataExpand.bind(this);
        this.getContentHeight = this.getContentHeight.bind(this);
        this.getButton = this.getButton.bind(this);
        this.getButtonText = this.getButtonText.bind(this);
        this.getClassName = this.getClassName.bind(this);
        this.setScrollPosition = this.setScrollPosition.bind(this);
    }

    /**
     * Note: this needs to be a getter method in order to update with fresh data when props change.
     */
    get resolvedProps(): ResolvedProps {
        const resolved = {...defaultOptions, ...this.props} as ResolvedProps;
        for (const key of Object.keys(defaultOptions) as (keyof Options)[]) {
            if (this.props[key] === undefined) {
                (resolved as Record<string, unknown>)[key] = defaultOptions[key];
            }
        }
        return resolved;
    }

    override componentDidMount() {
        this.shouldDataExpand();

        if (typeof ResizeObserver !== "undefined" && this.toggleContent) {
            this.resizeObserver = new ResizeObserver(() => this.shouldDataExpand());
            this.resizeObserver.observe(this.toggleContent);
        }
    }

    override componentDidUpdate(prevProps: Props) {
        if (prevProps.children !== this.props.children || prevProps.previewHeight !== this.props.previewHeight) {
            this.shouldDataExpand();
        }
    }

    override componentWillUnmount() {
        this.resizeObserver?.disconnect();
    }

    handleClick: MouseEventHandler<HTMLSpanElement> = (event) => {
        const thisObj = this;
        this.setState(
            {
                expanded: !this.state.expanded,
            },
            function () {
                const {expanded} = thisObj.state;
                const {onExpandClick, onCollapseClick, onClick} = thisObj.resolvedProps;

                if (onExpandClick) {
                    if (expanded) {
                        onExpandClick(event);
                    }
                }

                if (onCollapseClick) {
                    if (!expanded) {
                        onCollapseClick(event);
                    }
                }

                if (onClick) {
                    onClick(event);
                }

                thisObj.setScrollPosition();
            },
        );
    };

    setScrollPosition() {
        const {expanded} = this.state;

        if (!expanded) {
            const contentRect = this.toggleContent?.getBoundingClientRect();
            if (contentRect && contentRect.top < 0) {
                const offsetTop = Math.abs(
                    contentRect.top + (window.pageYOffset || document.documentElement.scrollTop),
                );

                window.scrollTo(0, offsetTop);
            }
        }
    }

    shouldDataExpand() {
        if (this.state.expanded) {
            return;
        }

        const body = this.toggleContent?.querySelector(".react-expand-collapse__body");
        const contentBodyRect = body ? body.getBoundingClientRect() : undefined;
        const previewHeight = Number.parseFloat(this.resolvedProps.previewHeight);

        if (!contentBodyRect || !Number.isFinite(previewHeight)) {
            return;
        }

        const shouldExpand = contentBodyRect.height > previewHeight + CONTENT_HEIGHT_TOLERANCE_PX;
        if (shouldExpand !== this.state.shouldExpand) {
            this.setState((state) => ({
                expanded: shouldExpand ? state.expanded : false,
                shouldExpand,
            }));
        }
    }

    getContentHeight() {
        const {expanded, shouldExpand} = this.state;

        const {previewHeight} = this.resolvedProps;

        if (expanded || !shouldExpand) {
            return "auto";
        }

        return previewHeight;
    }

    getButton() {
        const {expanded, shouldExpand} = this.state;

        const {collapse} = this.resolvedProps;

        if (shouldExpand) {
            if (!collapse && expanded) {
                return "";
            }

            const buttonText = this.getButtonText();

            return (
                <span
                    className="react-expand-collapse__button"
                    onClickCapture={this.handleClick}
                    aria-label={buttonText}
                    aria-expanded={expanded}
                    role="button"
                    style={{backgroundColor: this.resolvedProps.backgroundColor}}
                >
                    {!expanded && (
                        <div
                            style={{
                                content: "",
                                position: "absolute",
                                top: 0,
                                left: "-20px",
                                width: "20px",
                                height: "100%",
                                background: `linear-gradient(to right, transparent 0, ${this.resolvedProps.backgroundColor} 100%)`,
                            }}
                        />
                    )}
                    {buttonText}
                </span>
            );
        }

        return "";
    }

    getButtonText() {
        const {expanded} = this.state;

        const {expandText, collapseText, ellipsis, ellipsisText} = this.resolvedProps;

        let text = expanded ? collapseText : expandText;

        if (ellipsis) {
            text = !expanded ? `${ellipsisText + " " + text}` : text;
        }

        return text;
    }

    getClassName() {
        const {expanded} = this.state;

        const {className} = this.resolvedProps;

        const expandedClass = expanded ? "react-expand-collapse--expanded" : "";

        const classes = ["react-expand-collapse__content", expandedClass]
            .concat((className ?? "").split(" "))
            .join(" ");

        return classes;
    }

    setRef: React.RefCallback<HTMLDivElement> = (ref) => {
        this.toggleContent = ref;
    };

    override render() {
        const className = this.getClassName();
        const contentHeight = this.getContentHeight();
        const button = this.getButton();

        return (
            <div className={className} ref={this.setRef} style={{height: contentHeight, whiteSpace: "pre-line"}}>
                <div className="react-expand-collapse__body">{this.resolvedProps.children}</div>

                {button}
            </div>
        );
    }
}

export default ExpandCollapse;
